import React, { useState, useEffect, useRef } from "react";
import AxiosInstance from '../AxiosInstance';
import { FaCheck, FaTimes } from "react-icons/fa";
import Layout from './Layout';
import { ArrowLeftCircle } from "react-bootstrap-icons";
import { Button, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import ToastNotification from "../ToastNotification";

const WFHRequests = () => {
  const [wfhRequests, setWfhRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedWFH, setSelectedWFH] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const toastRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    AxiosInstance.get("/admin/wfhstatus")
      .then((res) => setWfhRequests(res.data))
      .catch(() => toastRef.current.showToast("Failed to load WFH requests", "danger"));

    AxiosInstance.get("/common/wfh-rejection-reasons")
      .then((res) => setRejectionReasons(res.data))
      .catch(() => toastRef.current.showToast("Failed to load rejection reasons", "danger"));
  }, []);

  const filterWFH = (status) => wfhRequests.filter(req => req.status === status);

  const handleAccept = (wfhId) => {
    AxiosInstance.post("/admin/accept-wfh", { wfhId })
      .then(() => {
        toastRef.current.showToast("WFH request accepted", "success");
        return AxiosInstance.get("/admin/wfhstatus");
      })
      .then(res => setWfhRequests(res.data))
      .catch(() => toastRef.current.showToast("Error accepting request", "danger"));
  };

  const handleReject = (wfhId) => {
    setSelectedWFH(wfhRequests.find(w => w.id === wfhId));
    setIsRejectModalOpen(true);
  };

  const confirmReject = () => {
    if (!rejectionReason) {
      toastRef.current.showToast("Select a rejection reason", "warning");
      return;
    }
    AxiosInstance.post("/admin/reject-wfh", {
      wfhId: selectedWFH.id,
      rejectReason: rejectionReason
    })
      .then(() => {
        toastRef.current.showToast("WFH request rejected", "success");
        return AxiosInstance.get("/admin/wfhstatus");
      })
      .then(res => {
        setWfhRequests(res.data);
        setIsRejectModalOpen(false);
        setRejectionReason('');
      })
      .catch(() => toastRef.current.showToast("Error rejecting request", "danger"));
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-IN');
  const formatDateTime = (date) => {
    const d = new Date(date);
    return `${d.toLocaleDateString('en-IN')} | ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <Layout heading="WFH Requests">
      <Row className="mb-3">
        <Col xs={12}>
          <Button className="btn btn-secondary mb-3" onClick={() => navigate(-1)}>
            <ArrowLeftCircle />
          </Button>
        </Col>
      </Row>
      <ul className="nav nav-tabs mb-3">
        {["pending", "accepted", "rejected"].map(tab => (
          <li className="nav-item" key={tab}>
            <button
              className={`nav-link ${activeTab === tab ? "active" : "text-dark"}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)} Requests
            </button>
          </li>
        ))}
      </ul>

      <div className="table-responsive">
        <table className="table table-striped text-center align-middle">
          <thead className="table-primary">
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>From</th>
              <th>To</th>
              <th>Applied On</th>
              <th>Reason</th>
              {activeTab === "pending" && <th>Actions</th>}
              {activeTab === "rejected" && <th>Rejection Reason</th>}
            </tr>
          </thead>
          <tbody>
            {filterWFH(activeTab).map(req => (
              <tr key={req.id}>
                <td>{req.full_name}</td>
                <td>{req.department_name}</td>
                <td>{formatDate(req.start_date)}</td>
                <td>{formatDate(req.end_date)}</td>
                <td>{formatDateTime(req.created_at)}</td>
                <td>{req.reason}</td>
                {activeTab === "pending" && (
                  <td>
                    <Button size="sm" variant="success" onClick={() => handleAccept(req.id)}><FaCheck /></Button>{' '}
                    <Button size="sm" variant="danger" onClick={() => handleReject(req.id)}><FaTimes /></Button>
                  </td>
                )}
                {activeTab === "rejected" && <td>{req.rejection_reason || "Not Provided"}</td>}
              </tr>
            ))}
            {filterWFH(activeTab).length === 0 && (
              <tr><td colSpan={7}>No {activeTab} requests</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject WFH Request</h5>
                <button type="button" className="btn-close" onClick={() => setIsRejectModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <label>Select Rejection Reason:</label>
                <select
                  className="form-control"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {rejectionReasons.map(reason => (
                    <option key={reason.id} value={reason.reason}>{reason.reason}</option>
                  ))}
                </select>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setIsRejectModalOpen(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmReject}>Reject</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastNotification ref={toastRef} />
    </Layout>
  );
};

export default WFHRequests;
